// @ts-nocheck
import * as React from "react"

import { cn } from "@/utils"

// Each component merges both `className` and `class` (with `class` last). Under preact/compat a
// vnode carrying both attributes drops `class`, so spreading a caller's `class="py-3"` alongside
// our merged `className` would silently lose the caller's classes (see the lucide-react note in
// AGENTS.md).

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, class: classAttr, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table ref={ref} className={cn("w-full caption-bottom", className, classAttr)} {...props} />
    </div>
  )
)
Table.displayName = "Table"

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, class: classAttr, ...props }, ref) => (
    <thead ref={ref} className={cn("[&_tr]:border-b", className, classAttr)} {...props} />
  )
)
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, class: classAttr, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className, classAttr)} {...props} />
  )
)
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, class: classAttr, ...props }, ref) => (
    <tfoot ref={ref} className={cn("border-t bg-muted/50 font-medium last:[&>tr]:border-b-0", className, classAttr)} {...props} />
  )
)
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, class: classAttr, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted", className, classAttr)}
      {...props}
    />
  )
)
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, class: classAttr, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-12 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:flex [&:has([role=checkbox])]:items-center",
        className,
        classAttr
      )}
      {...props}
    />
  )
)
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, class: classAttr, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        "align-middle [&:has([role=checkbox])]:px-2 [&:has([role=checkbox])]:flex [&:has([role=checkbox])]:items-center",
        className,
        classAttr
      )}
      {...props}
    />
  )
)
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, class: classAttr, ...props }, ref) => (
    <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className, classAttr)} {...props} />
  )
)
TableCaption.displayName = "TableCaption"

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
